/**
 * Members table — the PRIMARY view inside Client Intelligence.
 * Shows the full member roster with search, multi-axis filters,
 * sort, CSV export, and click-through to the Soul Sheet drawer.
 *
 * Per Dipali's spec:
 *   "When Nisha needs to check 'has Saikam filled his Soul form?'
 *    or 'which Bangalore members are dormant?' — she opens the
 *    Members tab and finds the answer in 3 seconds."
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import {
  Search, Download, Sparkles, RefreshCw, ArrowUp, ArrowDown,
  Cake, Heart, Phone, Mail, MapPin, ChevronRight, Send, X, ExternalLink, CheckCircle2,
} from 'lucide-react';

// Re-use SoulSheetDrawer from SoulSheetsPanel by lazy-importing it.
import { SoulSheetDrawer } from './SoulSheetsPanel';
import MembersCheatGuide from './MembersCheatGuide';

const PAGE_SIZE = 50;
const TIER_DOT = { RR: '#1C304A', RRS: '#5C5652', Unknown: '#B0B7BB' };

export default function MembersTable({ clientId, isCeo }) {
  const [members, setMembers] = useState([]);
  const [meta, setMeta] = useState({ total_members: 0, total_all: 0, soul_completed: 0 });
  const [loading, setLoading] = useState(false);

  // Filters
  const [q, setQ] = useState('');
  const [tier, setTier] = useState('');
  const [city, setCity] = useState('');
  const [soulFilter, setSoulFilter] = useState(''); // ''|'filled'|'unfilled'
  const [activity, setActivity] = useState('');    // ''|'active'|'dormant'|'never'
  // Default: only Primary + Active members (mirrors Zoho CRM "Concierge" filter)
  const [includeAddons, setIncludeAddons] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);

  // Sort + pagination
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);

  const [openMember, setOpenMember] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (q) params.q = q;
      if (tier) params.tier = tier;
      if (city) params.city = city;
      if (soulFilter === 'filled') params.soul_only = true;
      if (includeAddons) params.include_addons = true;
      if (includeInactive) params.include_inactive = true;
      const r = await api.get(`/soul-engine/${clientId}/members`, { params });
      setMembers(r.data?.members || []);
      setMeta({
        total_members: r.data?.total_members || 0,
        total_all: r.data?.total_all || 0,
        soul_completed: r.data?.soul_completed || 0,
      });
      setPage(1);
    } catch {
      setMembers([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId, tier, soulFilter, includeAddons, includeInactive]);
  useEffect(() => { const t = setTimeout(load, 280); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, city]);

  // ── Stable city list ────────────────────────────────────────────────
  // Cities are pulled from a SEPARATE one-shot fetch (limit=2000, no
  // filters) so the dropdown shows every city even when the user is
  // searching by name or scoping by tier. Rebuilding `cities` from
  // the filtered `members` array — as the previous build did — caused
  // cities to disappear from the dropdown the moment Dipali typed in
  // the search box, which made it impossible to switch city mid-search.
  const [allCities, setAllCities] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/soul-engine/${clientId}/members`,
          { params: { limit: 2000 } });
        if (cancelled) return;
        const set = new Set((r.data?.members || [])
          .map(m => m.city).filter(Boolean));
        setAllCities(Array.from(set).sort());
      } catch {
        // Silent — fall back to the per-load cities below if this fails.
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);
  const cities = useMemo(() => {
    if (allCities.length) return allCities;
    // Fallback when the one-shot hasn't loaded yet.
    return Array.from(new Set(members.map(m => m.city).filter(Boolean))).sort();
  }, [allCities, members]);

  // Apply soul-unfilled + activity filters client-side (server already handles soul-only)
  const filtered = useMemo(() => {
    let out = members;
    if (soulFilter === 'unfilled') out = out.filter(m => !m.soul_form_completed);
    if (activity) {
      out = out.filter(m => (m._activity || 'unknown') === activity);
    }
    // Sort. Special handling per column:
    //   • DOB / Anniversary — sort by MM-DD only so the table answers
    //     "who's next?" (sorting by full ISO date sorts by birth-year,
    //     which is meaningless for an upcoming-date list).
    //   • Soul / Sheet — boolean columns; ✓ ranks above ○.
    //   • Phone — strip non-digits before compare so "+91 9..." and
    //     "9..." rank identically.
    //   • Blank values always sink to the bottom regardless of dir.
    const dirMul = sortDir === 'asc' ? 1 : -1;
    const valueOf = (m) => {
      const raw = m[sortKey];
      if (sortKey === 'date_of_birth' || sortKey === 'date_of_anniversary') {
        // YYYY-MM-DD → MM-DD
        return (raw || '').toString().slice(5, 10);
      }
      if (sortKey === 'soul_form_completed') return raw ? '0' : '1';
      if (sortKey === 'has_profile') return raw ? '0' : '1';
      if (sortKey === 'phone') {
        return (raw || '').toString().replace(/\D+/g, '');
      }
      return (raw || '').toString().toLowerCase();
    };
    out = [...out].sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      // Blanks last (regardless of direction)
      if (!av && bv) return 1;
      if (av && !bv) return -1;
      if (av < bv) return -1 * dirMul;
      if (av > bv) return 1 * dirMul;
      return 0;
    });
    return out;
  }, [members, soulFilter, activity, sortKey, sortDir]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const exportCsv = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/soul-engine/${clientId}/members.csv?include_soul=true`;
    const token = localStorage.getItem('access_token') || '';
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = window.URL.createObjectURL(blob);
        a.download = `${clientId}_members_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
      });
  };

  const Th = ({ k, children, align = 'left' }) => (
    <th className={`py-2 px-3 text-${align} cursor-pointer hover:bg-gray-100 select-none`}
        onClick={() => {
          if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
          else { setSortKey(k); setSortDir('asc'); }
        }}
        title={`Sort by ${typeof children === 'string' ? children : k}`}>
      <span className="inline-flex items-center gap-0.5" style={{
        color: sortKey === k ? '#1B2A4A' : '#6B7280',
        fontWeight: sortKey === k ? 600 : 400,
      }}>
        {children}
        {/* Always render an indicator on sortable columns so users
            know they're clickable. Active column shows the current
            direction; idle columns show a faint up/down hint. */}
        {sortKey === k
          ? (sortDir === 'asc' ? <ArrowUp size={9} /> : <ArrowDown size={9} />)
          : <span style={{ color: '#C9CDD3', fontSize: 9, lineHeight: 1 }}>↕</span>}
      </span>
    </th>
  );

  const unfilled = meta.total_members - meta.soul_completed;

  return (
    <div className="space-y-3" data-testid="ci-members-panel">
      <MembersCheatGuide />
      {/* Headline */}
      <div className="flex flex-wrap items-baseline gap-3 px-1">
        <h2 className="text-base font-bold" style={{ color: '#1B2A4A' }}>
          {clientId.toUpperCase()} Members
        </h2>
        <span className="text-[12px]" style={{ color: '#6B7280' }}>
          <strong style={{ color: '#1B2A4A' }} data-testid="ci-members-total">{(meta.total_members || 0).toLocaleString('en-IN')}</strong>
          {(includeAddons || includeInactive) ? ' shown' : ' Concierge active'}
          {' · '}
          <strong style={{ color: '#1f5736' }}>{(meta.soul_completed || 0).toLocaleString('en-IN')}</strong> Soul forms completed
          {' · '}
          <strong style={{ color: '#A86B2C' }}>{Math.max(0, unfilled).toLocaleString('en-IN')}</strong> pending
          {meta.total_all > meta.total_members && (
            <>
              {' · '}
              <span style={{ color: '#9CA3AF' }} title="Includes add-ons + inactive">
                ({(meta.total_all || 0).toLocaleString('en-IN')} raw in CRM)
              </span>
            </>
          )}
        </span>
        <button onClick={load} data-testid="ci-members-refresh" className="ml-auto text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border bg-white" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Filter row */}
      <div className="bg-white rounded-lg border p-3 flex flex-wrap items-center gap-2" style={{ borderColor: '#E5E1DC' }}>
        <div className="inline-flex items-center gap-1 px-2 py-1.5 rounded border bg-white" style={{ borderColor: '#E5E1DC' }}>
          <Search size={12} style={{ color: '#9CA3AF' }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, phone…"
            data-testid="ci-members-search"
            className="text-[12px] outline-none bg-transparent w-56"
          />
        </div>
        <FilterPill label="Tier" value={tier} onChange={setTier} testId="ci-members-tier-filter" options={[
          { v: '', label: 'All tiers' }, { v: 'RR', label: 'RR' }, { v: 'RRS', label: 'RRS' },
        ]} />
        <FilterPill label="City" value={city} onChange={setCity} testId="ci-members-city-filter" options={[
          { v: '', label: 'All cities' }, ...cities.map(c => ({ v: c, label: c })),
        ]} />
        <FilterPill label="Soul Form" value={soulFilter} onChange={setSoulFilter} testId="ci-members-soul-filter" options={[
          { v: '', label: 'Soul: any' },
          { v: 'filled', label: 'Soul: filled ✓' },
          { v: 'unfilled', label: 'Soul: not filled' },
        ]} />
        <FilterPill label="Activity" value={activity} onChange={setActivity} testId="ci-members-activity-filter" options={[
          { v: '', label: 'Activity: any' },
          { v: 'active', label: 'Active (≤90d)' },
          { v: 'dormant', label: 'Dormant (>90d)' },
          { v: 'never', label: 'Never engaged' },
        ]} />
        <label className="inline-flex items-center gap-1 text-[11px] cursor-pointer select-none px-2 py-1.5 rounded border bg-white"
               style={{ borderColor: includeAddons ? '#1C304A' : '#E5E1DC', color: '#1B2A4A' }}
               title="By default we hide Secondary, Non Member, Client etc. — toggle to include them">
          <input type="checkbox" checked={includeAddons} onChange={(e) => setIncludeAddons(e.target.checked)}
                 data-testid="ci-members-include-addons" className="accent-[#1C304A]" />
          + Add-ons
        </label>
        <label className="inline-flex items-center gap-1 text-[11px] cursor-pointer select-none px-2 py-1.5 rounded border bg-white"
               style={{ borderColor: includeInactive ? '#1C304A' : '#E5E1DC', color: '#1B2A4A' }}
               title="By default we only show Membership_Status='Active' — toggle to include Inactive, Hold, Pending">
          <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)}
                 data-testid="ci-members-include-inactive" className="accent-[#1C304A]" />
          + Inactive
        </label>
        <button onClick={() => setShowInviteModal(true)} data-testid="ci-members-bulk-invite"
          className="ml-auto px-3 py-1.5 rounded-md text-[11px] font-semibold text-white inline-flex items-center gap-1"
          style={{ backgroundColor: '#1C304A' }}
          title="Generate Soul Form invites for unfilled members and push them to Zoho as draft tickets">
          <Send size={12} /> Send Soul Form Invites
        </button>
        <button onClick={exportCsv} data-testid="ci-members-export"
          className="px-3 py-1.5 rounded-md text-[11px] font-semibold text-white inline-flex items-center gap-1"
          style={{ backgroundColor: '#2A7B88' }}>
          <Download size={12} /> Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden" style={{ borderColor: '#E5E1DC' }}>
        <div className="overflow-y-auto" style={{ maxHeight: 600 }}>
          <table className="w-full text-[11.5px]">
            <thead className="sticky top-0 z-10" style={{ background: '#F7F5F2' }}>
              <tr>
                <Th k="name">Name</Th>
                <Th k="member_tier">Tier</Th>
                <Th k="city">City</Th>
                <Th k="phone">Phone</Th>
                <Th k="soul_form_completed" align="center">Soul</Th>
                <Th k="date_of_birth">DOB</Th>
                <Th k="date_of_anniversary">Anniv</Th>
                <Th k="has_profile">Sheet</Th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody data-testid="ci-members-tbody">
              {loading ? (
                <tr><td colSpan={9} className="py-6 text-center text-[12px]" style={{ color: '#9CA3AF' }}>Loading members…</td></tr>
              ) : pageRows.length === 0 ? (
                <tr><td colSpan={9} className="py-6 text-center text-[12px]" style={{ color: '#9CA3AF' }}>
                  No members match. Adjust filters or run a CRM sync if the table is empty.
                </td></tr>
              ) : pageRows.map((m) => (
                <tr
                  key={m.zoho_contact_id}
                  onClick={() => setOpenMember(m)}
                  data-testid={`ci-member-row-${m.zoho_contact_id}`}
                  className="border-t hover:bg-gray-50 cursor-pointer"
                  style={{ borderColor: '#F0EEEA' }}>
                  <td className="py-2 px-3 font-medium truncate" style={{ color: '#1B2A4A', maxWidth: 220 }}>
                    {m.name || '—'}
                    {m.lsm_name && m.lsm_name !== 'Admin' && (
                      <div className="text-[9.5px] mt-0.5" style={{ color: '#9CA3AF' }}>LSM: {m.lsm_name}</div>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{
                      background: TIER_DOT[m.member_tier] === '#1C304A' ? '#E8EAF6' : '#F4F1EB',
                      color: TIER_DOT[m.member_tier] || '#9CA3AF',
                    }}>{m.member_tier || '—'}</span>
                  </td>
                  <td className="py-2 px-3" style={{ color: '#6B7280' }}>{m.city || '—'}</td>
                  <td className="py-2 px-3 tabular-nums truncate" style={{ color: '#6B7280', maxWidth: 130 }}>{m.phone || '—'}</td>
                  <td className="py-2 px-3 text-center">
                    {m.soul_form_completed
                      ? <span style={{ color: '#1f5736', fontSize: 14 }}>✓</span>
                      : <span style={{ color: '#A86B2C', fontSize: 14 }}>○</span>}
                  </td>
                  <td className="py-2 px-3 tabular-nums" style={{ color: '#6B7280' }}>{(m.date_of_birth || '').slice(5, 10) || '—'}</td>
                  <td className="py-2 px-3 tabular-nums" style={{ color: '#6B7280' }}>{(m.date_of_anniversary || '').slice(5, 10) || '—'}</td>
                  <td className="py-2 px-3">
                    {m.has_profile
                      ? <span className="text-[10px] inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded" style={{ background: '#F0E7F8', color: '#5B2C82' }}>
                          <Sparkles size={9} /> Generated
                        </span>
                      : <span className="text-[10px]" style={{ color: '#9CA3AF' }}>—</span>}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <ChevronRight size={12} style={{ color: '#9CA3AF' }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div className="flex items-center gap-2 border-t px-3 py-2 text-[11px]" style={{ borderColor: '#F0EEEA', color: '#6B7280' }}>
            <span>Showing <strong style={{ color: '#1B2A4A' }}>{(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, total)}</strong> of <strong style={{ color: '#1B2A4A' }}>{total.toLocaleString('en-IN')}</strong></span>
            <div className="ml-auto flex items-center gap-1">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)} data-testid="ci-members-prev"
                className="px-2 py-0.5 rounded border bg-white disabled:opacity-40" style={{ borderColor: '#E5E1DC' }}>
                ← Prev
              </button>
              <span className="px-2">{page} / {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="ci-members-next"
                className="px-2 py-0.5 rounded border bg-white disabled:opacity-40" style={{ borderColor: '#E5E1DC' }}>
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {openMember && (
        <SoulSheetDrawer
          clientId={clientId}
          member={openMember}
          isCeo={isCeo}
          onClose={() => setOpenMember(null)}
          onProfileGenerated={() => load()}
        />
      )}

      {showInviteModal && (
        <BulkInviteModal
          clientId={clientId}
          unfilledCount={Math.max(0, meta.total_members - meta.soul_completed)}
          onClose={() => setShowInviteModal(false)}
          onDone={() => { setShowInviteModal(false); load(); }}
        />
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// BulkInviteModal — generate Soul Form invites in background + poll status
// ────────────────────────────────────────────────────────────────────────────
function BulkInviteModal({ clientId, unfilledCount, onClose, onDone }) {
  const [phase, setPhase] = useState('confirm'); // confirm | running | ready | error
  const [job, setJob] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  // Type-to-confirm for large batches. Dipali doctrine (30-Apr-2026):
  // never let a single click fire a job that touches more than 25
  // members. The user must type CONFIRM to proceed.
  const REQUIRE_TYPED_CONFIRM_ABOVE = 25;
  const needsTypedConfirm = unfilledCount > REQUIRE_TYPED_CONFIRM_ABOVE;
  const [typedConfirm, setTypedConfirm] = useState('');
  const confirmOk = !needsTypedConfirm || typedConfirm.trim().toUpperCase() === 'CONFIRM';

  const cancelJob = async () => {
    if (!job?.job_id) return;
    if (!confirm('Stop this bulk Soul Form invite run? Tickets already pushed stay in Zoho Desk.')) return;
    try {
      await api.post(`/soul-form-admin/jobs/${job.job_id}/cancel`);
      setErrorMsg('Cancellation requested — will stop within ~30 seconds.');
    } catch (e) {
      alert('Cancel failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const start = async () => {
    setPhase('running');
    try {
      const r = await api.post(`/soul-form-admin/${clientId}/invite/bulk`, {
        only_unfilled: true,
        push_to_zoho: true,
        origin: window.location.origin,
      });
      setJob({ ...r.data, completed: 0 });
      // Poll
      const jid = r.data.job_id;
      const poll = setInterval(async () => {
        try {
          const s = await api.get(`/soul-form-admin/jobs/${jid}`);
          setJob(s.data);
          if (s.data.status === 'ready') {
            clearInterval(poll);
            setPhase('ready');
          }
        } catch { /* keep polling */ }
      }, 2500);
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || 'Could not start the invite job.');
      setPhase('error');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: 'rgba(28, 48, 74, 0.55)', backdropFilter: 'blur(4px)' }}
         data-testid="bulk-invite-modal">
      <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full overflow-hidden">
        <div className="px-5 py-4 border-b flex items-center justify-between"
             style={{ borderColor: '#E5E1DC', background: '#1C304A', color: '#EEECE1' }}>
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase" style={{ color: '#C9A66B' }}>
              Concierge® · Soul Engine
            </div>
            <h3 className="text-base font-semibold mt-0.5">Send Soul Form Invites</h3>
          </div>
          <button onClick={onClose} data-testid="bulk-invite-close"
                  className="text-[#EEECE1] opacity-70 hover:opacity-100">
            <X size={18} />
          </button>
        </div>

        {phase === 'confirm' && (
          <div className="p-5 space-y-4">
            <p className="text-[13px] leading-relaxed" style={{ color: '#1B2A4A' }}>
              We'll mint a 30-day token for every unfilled member, ask Claude
              to draft a personalised invite (referencing their LSM and onboarding
              context), and push each as a <strong>draft ticket</strong> into Zoho
              Desk for the team to review and Reply.
            </p>
            <div className="rounded-md p-3" style={{ background: '#F7F5F2', border: '1px solid #E5E1DC' }}>
              <div className="text-[11px] tracking-wider uppercase" style={{ color: '#A86B2C' }}>
                Scope
              </div>
              <div className="text-2xl font-bold mt-1 tabular-nums" style={{ color: '#1B2A4A' }}>
                {unfilledCount.toLocaleString('en-IN')}
              </div>
              <div className="text-[11px]" style={{ color: '#6B7280' }}>
                members in <strong>{clientId.toUpperCase()}</strong> with no Soul Form on file
              </div>
            </div>
            <ul className="text-[11.5px] space-y-1" style={{ color: '#6B7280' }}>
              <li>• Tickets land in Zoho Desk as <strong>Open + Low priority</strong>.</li>
              <li>• Nothing leaves to the member until LSM clicks Reply.</li>
              <li>• If a token already exists for a member, it is reused.</li>
              <li>• Job runs in the background — you can close this safely.</li>
              <li>• Job is <strong>cancellable at any time</strong> — Cancel button appears during the run.</li>
            </ul>
            {needsTypedConfirm && (
              <div className="rounded-md p-3 border"
                    style={{ background: '#FFF4E0', borderColor: '#FDB97A' }}>
                <div className="text-[11px] font-semibold mb-1.5" style={{ color: '#7A5C2B' }}>
                  ⚠️ Large batch — please confirm
                </div>
                <div className="text-[11.5px] mb-2" style={{ color: '#5C4420' }}>
                  This will draft <strong>{unfilledCount.toLocaleString('en-IN')}</strong> Soul Form
                  invites and push them all to Zoho Desk. The job runs for roughly
                  <strong> {Math.ceil(unfilledCount * 6 / 60)} minutes</strong> in the background.
                  Type <strong>CONFIRM</strong> to proceed.
                </div>
                <input
                  type="text"
                  value={typedConfirm}
                  onChange={(e) => setTypedConfirm(e.target.value)}
                  data-testid="bulk-invite-typed-confirm"
                  placeholder="Type CONFIRM to unlock"
                  className="w-full text-[12px] px-3 py-1.5 rounded border uppercase tracking-wider"
                  style={{ borderColor: '#FDB97A', background: 'white', color: '#1B2A4A' }} />
              </div>
            )}
            <div className="flex items-center justify-end gap-2 pt-2">
              <button onClick={onClose} data-testid="bulk-invite-cancel"
                      className="px-4 py-2 text-[12px] rounded-md border bg-white"
                      style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
                Cancel
              </button>
              <button onClick={start} disabled={!unfilledCount || !confirmOk}
                      data-testid="bulk-invite-start"
                      className="px-4 py-2 text-[12px] font-semibold rounded-md text-white disabled:opacity-50"
                      style={{ background: '#1C304A' }}>
                Generate {unfilledCount.toLocaleString('en-IN')} drafts
              </button>
            </div>
          </div>
        )}

        {phase === 'running' && (
          <div className="p-6 text-center space-y-4">
            <div className="w-10 h-10 border-2 border-t-transparent rounded-full animate-spin mx-auto"
                 style={{ borderColor: '#1C304A', borderTopColor: 'transparent' }} />
            <div className="text-[13px]" style={{ color: '#1B2A4A' }}>
              Drafting <strong>{job?.completed || 0}</strong> of <strong>{job?.total || '…'}</strong> invites…
            </div>
            <div className="h-1.5 w-full rounded-full overflow-hidden" style={{ background: '#F0EEEA' }}>
              <div className="h-full transition-all"
                   style={{ width: `${job?.total ? ((job.completed || 0) / job.total) * 100 : 0}%`,
                            background: '#C9A66B' }} />
            </div>
            <p className="text-[11px]" style={{ color: '#6B7280' }}>
              Each draft includes a unique 30-day Soul Form link. You can close
              this dialog — the job runs in the background.
            </p>
            <div className="flex items-center justify-center gap-3 pt-1">
              <button onClick={onClose}
                      className="text-[11px] underline" style={{ color: '#6B7280' }}>
                Close — keep running
              </button>
              <span style={{ color: '#E5E1DC' }}>·</span>
              <button onClick={cancelJob}
                      data-testid="bulk-invite-stop"
                      className="text-[11px] font-semibold rounded-md px-3 py-1 border"
                      style={{ borderColor: '#A32D2D', color: '#A32D2D', background: 'white' }}>
                🛑 Stop this run
              </button>
            </div>
            {errorMsg && (
              <p className="text-[11px] mt-2" style={{ color: '#A86B2C' }}>
                {errorMsg}
              </p>
            )}
          </div>
        )}

        {phase === 'ready' && job && (
          <div className="p-5 space-y-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={20} style={{ color: '#1f5736' }} />
              <h4 className="text-[14px] font-semibold" style={{ color: '#1B2A4A' }}>
                {job.successes?.length || 0} drafts pushed to Zoho Desk
              </h4>
            </div>
            {job.failures?.length > 0 && (
              <p className="text-[11.5px]" style={{ color: '#A86B2C' }}>
                {job.failures.length} member(s) skipped — check the job log via the API.
              </p>
            )}
            <div className="max-h-60 overflow-y-auto rounded border text-[11px]"
                 style={{ borderColor: '#E5E1DC' }} data-testid="bulk-invite-results">
              {(job.successes || []).slice(0, 50).map(s => (
                <div key={s.member_id}
                     className="flex items-center gap-2 px-3 py-1.5 border-b"
                     style={{ borderColor: '#F0EEEA' }}>
                  <span className="truncate flex-1" style={{ color: '#1B2A4A' }}>{s.name}</span>
                  {s.ticket_url && (
                    <a href={s.ticket_url} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-0.5 text-[10px] font-medium"
                       style={{ color: '#2A7B88' }}>
                      Zoho <ExternalLink size={9} />
                    </a>
                  )}
                  <a href={s.link} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-0.5 text-[10px]"
                     style={{ color: '#6B7280' }}>
                    Soul Form <ExternalLink size={9} />
                  </a>
                </div>
              ))}
              {(job.successes || []).length > 50 && (
                <div className="px-3 py-1.5 text-[10px]" style={{ color: '#9CA3AF' }}>
                  +{job.successes.length - 50} more — listed in the API response.
                </div>
              )}
            </div>
            <p className="text-[11px]" style={{ color: '#6B7280' }}>
              Open Zoho Desk to review each draft, edit copy if you like, then
              click Reply. Soul Form responses auto-save into ScaleBoard the
              moment a member taps "Next".
            </p>
            <div className="flex justify-end pt-1">
              <button onClick={onDone} data-testid="bulk-invite-done"
                      className="px-4 py-2 text-[12px] font-semibold rounded-md text-white"
                      style={{ background: '#1C304A' }}>
                Done
              </button>
            </div>
          </div>
        )}

        {phase === 'error' && (
          <div className="p-5 space-y-3">
            <p className="text-[13px]" style={{ color: '#A23B3B' }}>{errorMsg}</p>
            <div className="flex justify-end">
              <button onClick={onClose}
                      className="px-4 py-2 text-[12px] font-semibold rounded-md text-white"
                      style={{ background: '#1C304A' }}>
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function FilterPill({ label, value, onChange, options, testId }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="text-[11px] px-2 py-1.5 rounded border bg-white" style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}>
      {options.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
    </select>
  );
}
